import React, { useMemo } from 'react';
import { CompassIcon, HeartIcon, LeafIcon, VerifiedIcon } from '../icons';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import AvatarMedium from '../../../../components/Avatar/Avatar';
import { getListingReviewStats } from '../reviewStats';
import css from './ImpactBar.module.css';

const IMPACT_TAGS = [
  {
    key: 'experience',
    titleId: 'ImpactBar.tag.experience',
    textId: 'listingPage.sectionValues.three',
    Icon: CompassIcon,
    iconSize: 22,
  },
  {
    key: 'support',
    titleId: 'listingPage.sectionValues.title.one',
    textId: 'listingPage.sectionValues.one',
    Icon: HeartIcon,
    iconSize: 22,
  },
  {
    key: 'sustain',
    titleId: 'listingPage.sectionValues.title.two',
    textId: 'listingPage.sectionValues.two',
    Icon: LeafIcon,
    iconSize: 22,
  },
];

const useImpactData = (currentListing, currentLanguage, reviews) => {
  const intl = useIntl();
  const publicData = currentListing?.attributes?.publicData || {};
  const host = currentListing?.author;
  const hostProfile = host?.attributes?.profile?.publicData || {};
  const { count: reviewsCount, rating: ratingValue } = getListingReviewStats(
    currentListing,
    reviews
  );

  const projectImpact =
    currentLanguage === 'de'
      ? hostProfile.projectImpact_de || hostProfile.projectImpact
      : hostProfile.projectImpact || hostProfile.projectImpact_de;

  const partnerYears = useMemo(() => {
    const createdAt = host?.attributes?.createdAt;
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    return Math.max(1, Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 365)));
  }, [host]);

  const hostDisplayName = host?.attributes?.profile?.displayName || '';
  const hostFirstName = hostDisplayName.split(' ')[0] || hostDisplayName;

  const hostAboutText =
    currentLanguage === 'en'
      ? hostProfile.aboutTheHost ||
        intl.formatMessage({ id: 'AboutProjectNew.placeholderHost' }, { hostName: hostFirstName })
      : hostProfile.aboutTheHost_de ||
        hostProfile.aboutTheHost ||
        intl.formatMessage({ id: 'AboutProjectNew.placeholderHost' }, { hostName: hostFirstName });

  const formattedRating = intl.formatNumber(ratingValue, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return {
    publicData,
    host,
    projectImpact,
    partnerYears,
    hostDisplayName,
    hostFirstName,
    hostAboutText,
    formattedRating,
    reviewsCount,
    ratingValue,
  };
};

export const ImpactTagsRow = () => (
  <section className={css.pillarBand} aria-label="Impact values">
    <div className={css.pillarInner}>
      <div className={css.tagsRow}>
        {IMPACT_TAGS.map(({ key, titleId, textId, Icon, iconSize }) => (
          <div key={key} className={css.tagItem}>
            <div className={css.tagIcon}>
              <Icon className={css.tagIconSvg} size={iconSize} aria-hidden="true" />
            </div>
            <div className={css.tagCopy}>
              <div className={css.tagTitle}>
                <FormattedMessage id={titleId} />
              </div>
              <div className={css.tagText}>
                <FormattedMessage id={textId} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ImpactStrip = ({ currentListing, currentLanguage, reviews = [] }) => {
  const { projectImpact, partnerYears, formattedRating, reviewsCount, ratingValue } = useImpactData(
    currentListing,
    currentLanguage,
    reviews
  );

  if (!projectImpact) {
    return null;
  }

  const stats = [];
  if (partnerYears) {
    stats.push(
      <div key="partner" className={css.statItem}>
        <div className={css.statValue}>
          <FormattedMessage id="ImpactBar.partnerYearsValue" values={{ years: partnerYears }} />
        </div>
        <div className={css.statLabelDesktop}>
          <FormattedMessage id="ImpactBar.partnerLabel" />
        </div>
        <div className={css.statLabelMobile}>
          <FormattedMessage id="ImpactBar.partnerLabelShort" />
        </div>
      </div>
    );
  }
  if (ratingValue > 0 && reviewsCount > 0) {
    stats.push(
      <div key="rating" className={css.statItem}>
        <div className={css.statValue}>{formattedRating} ★</div>
        <div className={css.statLabelDesktop}>
          <FormattedMessage id="ImpactBar.fromTravelers" values={{ count: reviewsCount }} />
        </div>
        <div className={css.statLabelMobile}>
          <FormattedMessage id="ImpactBar.reviewsCount" values={{ count: reviewsCount }} />
        </div>
      </div>
    );
  }

  return (
    <div className={css.impactCard} data-elbaction="visible:vis impactBar">
      <span className={css.impactCardOrbLarge} aria-hidden="true" />
      <span className={css.impactCardOrbSmall} aria-hidden="true" />
      <div className={css.impactEyebrow}>
        <FormattedMessage id="ImpactBar.supportsLabel" />
      </div>
      <div className={css.impactHeadline}>{projectImpact}</div>
      {stats.length > 0 && (
        <div className={css.statsRow}>
          {stats.map((stat, i) => (
            <React.Fragment key={stat.key}>
              {i > 0 && <div className={css.statDivider} aria-hidden="true" />}
              {stat}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const HostTeaser = ({ currentListing, currentLanguage, reviews = [] }) => {
  const { host, hostDisplayName, hostFirstName, hostAboutText } = useImpactData(
    currentListing,
    currentLanguage,
    reviews
  );

  if (!hostDisplayName) {
    return null;
  }

  return (
    <div className={css.hostTeaser}>
      <div className={css.hostAvatarWrap}>
        <AvatarMedium className={css.hostAvatar} user={host} disableProfileLink />
        <span className={css.verifiedBadge}>
          <VerifiedIcon className={css.verifiedBadgeSvg} size={18} aria-hidden="true" />
        </span>
      </div>
      <div className={css.hostTeaserContent}>
        <div className={css.hostTeaserLabel}>
          <FormattedMessage id="ImpactBar.hostTeaserLabel" />
        </div>
        <div className={css.hostTeaserNameRow}>
          <span className={css.hostTeaserName}>
            <span className={css.hostTeaserNameMobile}>{hostFirstName}</span>
            <span className={css.hostTeaserNameDesktop}>{hostFirstName}</span>
          </span>
          <span className={css.hostVerifiedPill}>
            <VerifiedIcon className={css.verifiedPillSvg} size={16} aria-hidden="true" />
            <FormattedMessage id="ImpactBar.verified" />
          </span>
        </div>
        <div className={css.hostQuote}>„{hostAboutText}"</div>
      </div>
    </div>
  );
};

const ImpactBar = props => (
  <>
    <ImpactTagsRow />
    <ImpactStrip {...props} />
    <HostTeaser {...props} />
  </>
);

export default ImpactBar;
